import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { STORIES, NARRATORS, chapterLabel } from "../data/book";
import { useLang } from "../i18n";
import { useAuth } from "../auth";
import { loadProgress } from "../progress";
import Reveal from "../components/Reveal";
import StoryCard from "../components/StoryCard";
import Marquee from "../components/Marquee";

const FILTERS = ["all", "giulia", "alex", "duet"];

const Stories = () => {
  const { lang, t } = useLang();
  const { user } = useAuth();
  const [filter, setFilter] = useState("all");
  const [progress, setProgress] = useState(null);
  const list = filter === "all" ? STORIES : STORIES.filter((s) => s.narrator === filter);

  useEffect(() => {
    loadProgress(user).then(setProgress);
  }, [user]);

  const lastStory = progress?.last != null ? STORIES.find((s) => s.id === progress.last) : null;
  const lastPct = lastStory ? progress.chapters?.[String(lastStory.id)]?.percent || 0 : 0;
  const readCount = Object.values(progress?.chapters || {}).filter((c) => c.read).length;

  return (
    <main className="pt-16" data-testid="stories-page">
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-20 pb-12">
        <Reveal>
          <p className="font-mono2 text-xs tracking-[0.35em] uppercase text-ember mb-5">{t("stories_page_sub")}</p>
          <h1 className="font-display font-extrabold uppercase text-4xl sm:text-6xl lg:text-7xl tracking-tight leading-none" data-testid="stories-title">
            {t("stories_page_title")}
          </h1>
        </Reveal>
        <Reveal delay={0.15} className="mt-10">
          <div className="flex flex-wrap gap-3" data-testid="narrator-filters">
            {FILTERS.map((f) => {
              const active = filter === f;
              const nar = f !== "all" ? NARRATORS[f] : null;
              return (
                <button
                  key={f}
                  data-testid={`filter-${f}`}
                  onClick={() => setFilter(f)}
                  className={`rounded-full px-5 py-2.5 text-xs font-mono2 tracking-[0.2em] uppercase border transition-all duration-300 ${
                    active ? "text-black" : "text-mist hover:text-white"
                  }`}
                  style={
                    active
                      ? { background: nar ? nar.color : "#F4F3F8", borderColor: "transparent" }
                      : { borderColor: nar ? `${nar.color}55` : "rgba(255,255,255,0.15)" }
                  }
                >
                  {f === "all" ? t("filter_all") : nar[lang] ?? nar.it}
                </button>
              );
            })}
          </div>
        </Reveal>
      </section>

      {lastStory && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-10">
          <Reveal>
            <Link
              to={`/storia/${lastStory.id}?v=read`}
              data-testid="continue-reading-banner"
              className="group flex items-center justify-between gap-4 rounded-2xl border border-gold/40 bg-gold/5 px-6 py-5 hover:bg-gold/10 transition-colors"
            >
              <div>
                <p className="font-mono2 text-[10px] tracking-[0.3em] uppercase text-gold mb-1">{t("continue_reading")}</p>
                <p className="font-display font-bold text-lg">{chapterLabel(lastStory, lang)} <span className="text-mist font-normal text-sm">· {lastPct}% · {readCount}/28 {t("progress_of")}</span></p>
              </div>
              <ArrowRight className="w-5 h-5 text-gold transition-transform group-hover:translate-x-1.5" />
            </Link>
          </Reveal>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-28">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="stories-grid">
          {list.map((s, i) => (
            <Reveal key={`${filter}-${s.id}`} delay={Math.min(i, 8) * 0.05}>
              <StoryCard story={s} progress={progress?.chapters} />
            </Reveal>
          ))}
        </div>
      </section>
      <Marquee text={t("marquee")} />
    </main>
  );
};

export default Stories;
